import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Send } from "lucide-react";
import { eventsApi } from "@/api/events";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const SAMPLE = `{\n  "userId": "u_123"\n}`;

/** Topbar "Send event" action — ingest a test event and trigger matching functions. */
export function SendEventDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [dataText, setDataText] = useState(SAMPLE);
  const [jsonErr, setJsonErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ event_id: string; runs_created: number } | null>(null);
  const qc = useQueryClient();
  const nav = useNavigate();

  const send = useMutation({
    mutationFn: () => {
      const t = dataText.trim();
      const data: unknown = t ? JSON.parse(t) : {};
      return eventsApi.send(name.trim(), data);
    },
    onSuccess: (res) => {
      setResult(res);
      // Refresh anything that shows events/runs.
      void qc.invalidateQueries({ queryKey: ["events"] });
      void qc.invalidateQueries({ queryKey: ["home-runs"] });
      void qc.invalidateQueries({ queryKey: ["runs"] });
    },
  });

  function reset() {
    setName("");
    setDataText(SAMPLE);
    setJsonErr(null);
    setResult(null);
    send.reset();
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setJsonErr(null);
    const t = dataText.trim();
    if (t) {
      try { JSON.parse(t); } catch { setJsonErr("Data must be valid JSON."); return; }
    }
    send.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5 bg-brand text-primary-foreground hover:bg-brand/90">
          <Send size={14} /> Send event
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send event</DialogTitle>
          <DialogDescription>
            Ingest an event into your environment and trigger any function whose trigger matches its name.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-lg border border-ok/25 bg-ok/10 px-3 py-2.5 text-sm text-ok">
              <CheckCircle2 size={15} />
              Event sent · {result.runs_created} run{result.runs_created === 1 ? "" : "s"} triggered
            </div>
            <div className="font-mono text-xs text-muted-foreground">
              event · <span className="break-all text-foreground">{result.event_id}</span>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={reset}>Send another</Button>
              <Button
                className="bg-brand text-primary-foreground hover:bg-brand/90"
                onClick={() => {
                  const id = result.event_id;
                  setOpen(false);
                  reset();
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  void nav({ to: "/events/$id" as any, params: { id } as any });
                }}
              >
                View event
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ev-name">Event name</Label>
              <Input
                id="ev-name"
                required
                placeholder="user.signup"
                className="font-mono text-[13px]"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ev-data">Data (JSON)</Label>
              <Textarea
                id="ev-data"
                rows={8}
                spellCheck={false}
                className="scroll-thin font-mono text-[13px]"
                value={dataText}
                onChange={(e) => setDataText(e.target.value)}
              />
              {jsonErr && <p className="text-sm text-fail">{jsonErr}</p>}
            </div>
            {send.isError && <p className="text-sm text-fail">Failed to send. Check the event name and try again.</p>}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                disabled={send.isPending || !name.trim()}
                className="gap-1.5 bg-brand text-primary-foreground hover:bg-brand/90"
              >
                <Send size={14} /> {send.isPending ? "Sending…" : "Send event"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
